import React, { useRef, useEffect, useState, useCallback } from 'react';
import { StyleSheet, Animated, Easing, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';

const BAR_WIDTH = 3;
const BAR_GAP = 3;
const BAR_PITCH = BAR_WIDTH + BAR_GAP;
const BAR_MAX_HEIGHT = 26;
const BAR_MIN_SCALE = 3 / BAR_MAX_HEIGHT;
// Upper bound on bars we ever allocate; the visible count follows the width.
const MAX_BARS = 120;
const FRAME_MS = 100;

/**
 * Speech RMS sits roughly in 0.03–0.3. Lift that range so normal talking
 * fills most of the bar height while room noise stays near the floor.
 */
function normalizeLevel(level) {
  const boosted = Math.min(1, Math.max(0, level) * 3.2);
  return Math.pow(boosted, 0.75);
}

/**
 * Rolling voice-memo style waveform. Newest sample on the right, older
 * samples trail off to the left and fade. Bars animate via `scaleY` so the
 * work stays on the native/compositor thread.
 */
function DictateWaveform({ audioLevels, isProcessing = false, isStarting = false }) {
  const { colors } = useTheme();
  const [barCount, setBarCount] = useState(0);
  // Lazy: allocating 120 Animated.Values on every render (only to be thrown
  // away by useRef) is measurable at a 10Hz update rate.
  const barAnimsRef = useRef(null);
  if (barAnimsRef.current === null) {
    barAnimsRef.current = Array.from({ length: MAX_BARS }, () => new Animated.Value(BAR_MIN_SCALE));
  }
  const barAnims = barAnimsRef.current;
  const idleAnimRef = useRef(null);
  if (idleAnimRef.current === null) {
    idleAnimRef.current = new Animated.Value(0);
  }
  const idleAnim = idleAnimRef.current;
  const isIdleVisual = isProcessing || isStarting;

  const handleLayout = useCallback((e) => {
    const width = e?.nativeEvent?.layout?.width || 0;
    const next = Math.max(0, Math.min(MAX_BARS, Math.floor(width / BAR_PITCH)));
    setBarCount((prev) => (prev === next ? prev : next));
  }, []);

  // Live levels → bar scales. Right-align the history so the newest reading
  // is always the last bar; pad with silence when the history is shorter.
  useEffect(() => {
    if (isIdleVisual || barCount === 0) return;
    const levels = Array.isArray(audioLevels) ? audioLevels : [];
    const animations = [];
    for (let i = 0; i < barCount; i++) {
      const sampleIndex = levels.length - barCount + i;
      const level = sampleIndex >= 0 ? levels[sampleIndex] || 0 : 0;
      const scale = Math.max(BAR_MIN_SCALE, normalizeLevel(level));
      animations.push(
        Animated.timing(barAnims[i], {
          toValue: scale,
          duration: FRAME_MS,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        })
      );
    }
    const group = Animated.parallel(animations);
    group.start();
    return () => group.stop();
  }, [audioLevels, barCount, barAnims, isIdleVisual]);

  // Starting / transcribing: settle bars to a low resting line and breathe.
  useEffect(() => {
    if (!isIdleVisual) {
      idleAnim.stopAnimation();
      idleAnim.setValue(0);
      return;
    }
    const settle = Animated.parallel(
      barAnims.slice(0, Math.max(barCount, 1)).map((anim) =>
        Animated.timing(anim, {
          toValue: BAR_MIN_SCALE * 1.6,
          duration: 260,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        })
      )
    );
    settle.start();
    const breathe = Animated.loop(
      Animated.sequence([
        Animated.timing(idleAnim, { toValue: 1, duration: 900, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
        Animated.timing(idleAnim, { toValue: 0, duration: 900, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
      ])
    );
    breathe.start();
    return () => {
      settle.stop();
      breathe.stop();
    };
  }, [isIdleVisual, barCount, barAnims, idleAnim]);

  const barColor = isIdleVisual ? colors.text_color_tertiary : colors.accent_color_primary;
  const containerOpacity = idleAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 0.45] });

  return (
    <View style={styles.container} onLayout={handleLayout} pointerEvents="none">
      <Animated.View style={[styles.bars, { opacity: containerOpacity }]}>
        {Array.from({ length: barCount }, (_, i) => {
          // Older samples (left) fade so the eye reads the newest speech.
          const age = barCount > 1 ? i / (barCount - 1) : 1;
          const opacity = 0.28 + age * 0.72;
          return (
            <Animated.View
              key={i}
              style={[
                styles.bar,
                {
                  backgroundColor: barColor,
                  opacity,
                  transform: [{ scaleY: barAnims[i] }],
                },
              ]}
            />
          );
        })}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: BAR_MAX_HEIGHT + 8,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  bars: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: BAR_MAX_HEIGHT,
  },
  bar: {
    width: BAR_WIDTH,
    height: BAR_MAX_HEIGHT,
    marginLeft: BAR_GAP,
    borderRadius: BAR_WIDTH / 2,
  },
});

export default React.memo(DictateWaveform);
